document.addEventListener('DOMContentLoaded', function () {
    'use strict';

    function attachAutoplayPause(el, { pause, resume }) {
        el.addEventListener('mouseenter', pause);
        el.addEventListener('mouseleave', resume);
        el.addEventListener('focusin', pause);
        el.addEventListener('focusout', resume);
        document.addEventListener('visibilitychange', function () {
            if (document.hidden) {
                pause();
            } else {
                resume();
            }
        });
    }

    function easeInOut(t) {
        return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
    }

    /**
     * Flexbox scroll carousel with drag/momentum, button nav, optional
     * infinite loop + centered/peek slides, and progressbar pagination.
     * Free-scrolling -- no forced snap-to-slide while dragging/scrolling;
     * only button/autoplay navigation animates to an exact slide position.
     * Used by .grid-swiper, .quote-swiper and .media-gallery-caruousel.
     */
    function initGridCarousel(el, opts = {}) {
        const { loop = false, centered = false, autoplay = false, speed = 600 } = opts;

        const wrapper = el.querySelector('.swiper-wrapper');
        if (!wrapper) return;

        const realSlides = [...wrapper.querySelectorAll('.swiper-slide')];
        const realCount = realSlides.length;
        if (!realCount) return;

        const prevBtn = el.querySelector('.swiper-button-prev');
        const nextBtn = el.querySelector('.swiper-button-next');
        const pagination = el.querySelector('.swiper-pagination');
        const controls = el.querySelector('.grid-navigation');

        function updateControlsVisibility() {
            // Tailwind's `flex`/`block` utility classes on these elements have
            // no `!important`, but a plain inline style can still lose to any
            // author-origin `!important` rule elsewhere -- set the property
            // with `important` priority directly so this never silently no-ops
            // if a `!`-prefixed Tailwind utility is added to these elements later.
            const overflowing = wrapper.scrollWidth > wrapper.clientWidth + 1;
            [controls, prevBtn, nextBtn, pagination].forEach((elToToggle) => {
                if (!elToToggle) return;
                if (overflowing) {
                    elToToggle.style.removeProperty('display');
                } else {
                    elToToggle.style.setProperty('display', 'none', 'important');
                }
            });
        }

        let fillEl = null;
        if (pagination) {
            fillEl = pagination.querySelector('.swiper-pagination-progressbar-fill');
            if (!fillEl) {
                fillEl = document.createElement('span');
                fillEl.className = 'swiper-pagination-progressbar-fill';
                pagination.appendChild(fillEl);
            }
        }

        let cloneCount = 0;
        if (loop && realCount > 1) {
            const firstWidth = realSlides[0].getBoundingClientRect().width || 1;
            cloneCount = Math.min(realCount, Math.max(2, Math.ceil(wrapper.clientWidth / firstWidth) + 1));

            realSlides.slice(-cloneCount).reverse().forEach((slide) => {
                const clone = slide.cloneNode(true);
                clone.setAttribute('aria-hidden', 'true');
                clone.setAttribute('tabindex', '-1');
                clone.classList.add('swiper-slide-duplicate');
                wrapper.insertBefore(clone, wrapper.firstChild);
            });
            realSlides.slice(0, cloneCount).forEach((slide) => {
                const clone = slide.cloneNode(true);
                clone.setAttribute('aria-hidden', 'true');
                clone.setAttribute('tabindex', '-1');
                clone.classList.add('swiper-slide-duplicate');
                wrapper.appendChild(clone);
            });
        }

        const allSlides = [...wrapper.querySelectorAll('.swiper-slide')];
        let current = loop ? cloneCount : 0;
        let animRaf = null;

        function paddingLeft() {
            return parseFloat(getComputedStyle(wrapper).paddingLeft) || 0;
        }

        function slideMetrics() {
            const wrapRect = wrapper.getBoundingClientRect();
            return allSlides.map((s) => {
                const r = s.getBoundingClientRect();
                return { left: r.left - wrapRect.left + wrapper.scrollLeft, width: r.width };
            });
        }

        function scrollTargetFor(idx, metrics) {
            const m = metrics[idx];
            if (!m) return wrapper.scrollLeft;
            return centered
                ? m.left - (wrapper.clientWidth - m.width) / 2
                : m.left - paddingLeft();
        }

        function findNearestIndex(metrics) {
            const ref = centered
                ? wrapper.scrollLeft + wrapper.clientWidth / 2
                : wrapper.scrollLeft + paddingLeft();
            let best = 0;
            let bestDist = Infinity;
            metrics.forEach((m, i) => {
                const anchor = centered ? m.left + m.width / 2 : m.left;
                const dist = Math.abs(anchor - ref);
                if (dist < bestDist) {
                    bestDist = dist;
                    best = i;
                }
            });
            return best;
        }

        function setProgress(ratio) {
            if (!fillEl) return;
            fillEl.style.transform = `scaleX(${Math.max(0, Math.min(1, ratio))})`;
        }

        function updateButtons() {
            if (loop) {
                prevBtn?.setAttribute('aria-disabled', 'false');
                nextBtn?.setAttribute('aria-disabled', 'false');
                return;
            }
            const max = wrapper.scrollWidth - wrapper.clientWidth;
            prevBtn?.setAttribute('aria-disabled', wrapper.scrollLeft <= 1 ? 'true' : 'false');
            nextBtn?.setAttribute('aria-disabled', wrapper.scrollLeft >= max - 1 ? 'true' : 'false');
        }

        function updateActive(idx) {
            if (!centered) return;
            allSlides.forEach((s, i) => s.classList.toggle('swiper-slide-active', i === idx));
        }

        function animateTo(target, duration) {
            cancelAnimationFrame(animRaf);
            const startPos = wrapper.scrollLeft;
            const delta = target - startPos;
            if (Math.abs(delta) < 1 || duration <= 0) {
                wrapper.scrollLeft = target;
                return;
            }
            const startTime = performance.now();
            function tick(now) {
                const t = Math.min(1, (now - startTime) / duration);
                wrapper.scrollLeft = startPos + delta * easeInOut(t);
                if (t < 1) {
                    animRaf = requestAnimationFrame(tick);
                }
            }
            animRaf = requestAnimationFrame(tick);
        }

        function settle() {
            const metrics = slideMetrics();
            const rawIdx = findNearestIndex(metrics);
            const isClone = loop && (rawIdx < cloneCount || rawIdx >= cloneCount + realCount);
            const idx = isClone
                ? cloneCount + (((rawIdx - cloneCount) % realCount) + realCount) % realCount
                : rawIdx;

            // Only force a scroll correction when we actually landed on a loop
            // clone (an invisible seamless-wrap jump). Otherwise leave the
            // scroll position exactly where the drag/momentum/wheel left it --
            // no snap-to-slide correction.
            if (isClone) {
                cancelAnimationFrame(animRaf);
                wrapper.scrollLeft = scrollTargetFor(idx, metrics);
            }

            current = idx;
            updateActive(idx);
            updateButtons();
            if (loop && fillEl) {
                const realIdx = idx - cloneCount;
                setProgress(realCount > 1 ? realIdx / (realCount - 1) : 0);
            }
        }

        function go(idx) {
            const metrics = slideMetrics();
            const clampedIdx = loop ? idx : Math.max(0, Math.min(idx, allSlides.length - 1));
            animateTo(scrollTargetFor(clampedIdx, metrics), speed);
        }

        prevBtn?.addEventListener('click', () => go(current - 1));
        nextBtn?.addEventListener('click', () => go(current + 1));

        let scrollSettleTimer = null;
        wrapper.addEventListener('scroll', () => {
            if (fillEl && !loop) {
                const max = wrapper.scrollWidth - wrapper.clientWidth;
                setProgress(max > 0 ? wrapper.scrollLeft / max : 0);
            }
            clearTimeout(scrollSettleTimer);
            scrollSettleTimer = setTimeout(settle, 120);
        }, { passive: true });

        // Drag / swipe with velocity-based momentum. Pointer capture is only
        // taken once the pointer has actually moved past DRAG_THRESHOLD --
        // capturing on every pointerdown retargets the subsequent click away
        // from the actual element under the pointer (here, the slide's own
        // <a> tag), silently breaking taps that don't drag.
        const DRAG_THRESHOLD = 8;
        let dragging = false;
        let activePointerId = null;
        let startX = 0;
        let startScroll = 0;
        let lastX = 0;
        let lastT = 0;
        let velocity = 0;
        let momentumRaf = null;

        wrapper.addEventListener('pointerdown', (e) => {
            if (e.button !== 0 && e.pointerType === 'mouse') return;
            activePointerId = e.pointerId;
            dragging = false;
            cancelAnimationFrame(momentumRaf);
            cancelAnimationFrame(animRaf);
            startX = e.pageX;
            startScroll = wrapper.scrollLeft;
            lastX = e.pageX;
            lastT = performance.now();
            velocity = 0;
        });

        wrapper.addEventListener('pointermove', (e) => {
            if (e.pointerId !== activePointerId) return;
            if (!dragging) {
                if (Math.abs(e.pageX - startX) < DRAG_THRESHOLD) return;
                dragging = true;
                wrapper.setPointerCapture(e.pointerId);
            }
            const now = performance.now();
            const dt = Math.max(1, now - lastT);
            velocity = velocity * 0.6 + ((lastX - e.pageX) / dt) * 0.4;
            lastX = e.pageX;
            lastT = now;
            wrapper.scrollLeft = startScroll - (e.pageX - startX);
        });

        function endDrag() {
            activePointerId = null;
            if (!dragging) return;
            dragging = false;
            const friction = 0.93;
            function momentum() {
                velocity *= friction;
                wrapper.scrollLeft += velocity * 16;
                if (Math.abs(velocity) > 0.3) {
                    momentumRaf = requestAnimationFrame(momentum);
                } else {
                    settle();
                }
            }
            momentumRaf = requestAnimationFrame(momentum);
        }

        wrapper.addEventListener('pointerup', endDrag);
        wrapper.addEventListener('pointercancel', endDrag);
        wrapper.addEventListener('dragstart', (e) => e.preventDefault());

        let resizeTimer = null;
        new ResizeObserver(() => {
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(() => {
                wrapper.scrollLeft = scrollTargetFor(current, slideMetrics());
                updateButtons();
                updateControlsVisibility();
            }, 150);
        }).observe(wrapper);

        let autoplayHandle = null;
        function restartAutoplay() {
            if (!autoplay) return;
            clearInterval(autoplayHandle);
            autoplayHandle = setInterval(() => go(current + 1), autoplay);
        }
        if (autoplay) {
            restartAutoplay();
            attachAutoplayPause(el, {
                pause: () => clearInterval(autoplayHandle),
                resume: restartAutoplay,
            });
        }

        // Initial position: skip past the head clones, no animation.
        wrapper.scrollLeft = scrollTargetFor(current, slideMetrics());
        updateActive(current);
        updateButtons();
        updateControlsVisibility();
    }

    /**
     * Single-active-slide carousel: opacity crossfade ('fade') or
     * translateX ('slide'). Used by .media-gallery-slideshow and the
     * tankbar-multiblock plugin's .multiblock-gallery.
     */
    function initFadeCarousel(el, opts = {}) {
        const { mode = 'fade', navigation = true, autoplay = false, speed = 600, draggable = true } = opts;

        const wrapper = el.querySelector('.swiper-wrapper');
        const slides = wrapper ? [...wrapper.querySelectorAll('.swiper-slide')] : [];
        if (!slides.length) return;

        let current = 0;
        slides[0].classList.add('swiper-slide-active');

        if (mode === 'slide') {
            wrapper.classList.add('carousel-mode-slide');
            wrapper.style.transitionDuration = `${speed}ms`;
        } else {
            wrapper.classList.add('carousel-mode-fade');
            requestAnimationFrame(() => {
                slides.forEach((s) => {
                    s.style.transitionDuration = `${speed}ms`;
                });
            });
        }

        function applyTransform() {
            if (mode === 'slide') {
                wrapper.style.transform = `translateX(-${current * 100}%)`;
            }
        }

        const prevBtn = navigation ? el.querySelector('.swiper-button-prev') : null;
        const nextBtn = navigation ? el.querySelector('.swiper-button-next') : null;

        function updateButtons() {
            prevBtn?.setAttribute('aria-disabled', current <= 0 ? 'true' : 'false');
            nextBtn?.setAttribute('aria-disabled', current >= slides.length - 1 ? 'true' : 'false');
        }

        function go(idx) {
            const next = Math.max(0, Math.min(idx, slides.length - 1));
            if (next === current) return;
            slides[current].classList.remove('swiper-slide-active');
            slides[next].classList.add('swiper-slide-active');
            current = next;
            applyTransform();
            updateButtons();
            restartAutoplay();
        }

        prevBtn?.addEventListener('click', () => go(current - 1));
        nextBtn?.addEventListener('click', () => go(current + 1));
        updateButtons();

        if (draggable) {
            const DRAG_THRESHOLD = 8;
            let dragging = false;
            let activePointerId = null;
            let startX = 0;
            wrapper.addEventListener('pointerdown', (e) => {
                if (e.button !== 0 && e.pointerType === 'mouse') return;
                activePointerId = e.pointerId;
                dragging = false;
                startX = e.clientX;
            });
            wrapper.addEventListener('pointermove', (e) => {
                if (e.pointerId !== activePointerId || dragging) return;
                if (Math.abs(e.clientX - startX) < DRAG_THRESHOLD) return;
                dragging = true;
                wrapper.setPointerCapture(e.pointerId);
            });
            wrapper.addEventListener('pointerup', (e) => {
                activePointerId = null;
                if (!dragging) return;
                dragging = false;
                const diff = startX - e.clientX;
                if (Math.abs(diff) > 50) go(current + (diff > 0 ? 1 : -1));
            });
            wrapper.addEventListener('dragstart', (e) => e.preventDefault());
        }

        let autoplayHandle = null;
        function restartAutoplay() {
            if (!autoplay) return;
            clearInterval(autoplayHandle);
            autoplayHandle = setInterval(() => go(current + 1), autoplay);
        }
        if (autoplay) {
            restartAutoplay();
            attachAutoplayPause(el, {
                pause: () => clearInterval(autoplayHandle),
                resume: restartAutoplay,
            });
        }
    }

    document.querySelectorAll('.grid-swiper').forEach((el) => initGridCarousel(el));

    document.querySelectorAll('.quote-swiper').forEach((el) => {
        initGridCarousel(el, { loop: true, centered: true, speed: 1000 });
    });

    document.querySelectorAll('.media-gallery-caruousel').forEach((el) => {
        const swiper = el.querySelector('.swiper');
        if (!swiper) return;
        initGridCarousel(el, {
            loop: true,
            centered: true,
            speed: parseInt(swiper.dataset.transitionDuration) || 700,
            autoplay: swiper.dataset.galleryAutoplay === 'true'
                ? (parseInt(swiper.dataset.intervalDuration) || 6000)
                : false,
        });
    });

    document.querySelectorAll('.media-gallery-slideshow').forEach((el) => {
        const swiper = el.querySelector('.swiper');
        if (!swiper) return;
        initFadeCarousel(el, {
            mode: 'fade',
            speed: parseInt(swiper.dataset.transitionDuration) || 700,
            autoplay: swiper.dataset.galleryAutoplay === 'true'
                ? (parseInt(swiper.dataset.intervalDuration) || 6000)
                : false,
            navigation: swiper.dataset.galleryNavigation === 'true',
        });
    });

    document.querySelectorAll('.multiblock-gallery').forEach((el) => {
        const swiper = el.querySelector('.swiper');
        if (!swiper) return;
        const galleryButtons = swiper.dataset.galleryButtons === 'true';
        initFadeCarousel(el, {
            mode: swiper.dataset.transitionStyle === 'slide' ? 'slide' : 'fade',
            speed: parseInt(swiper.dataset.transitionDuration) || 1500,
            autoplay: galleryButtons ? false : (parseInt(swiper.dataset.intervalDuration) || 6000),
            navigation: galleryButtons,
            draggable: galleryButtons,
        });
    });
});
